/* eslint-disable no-case-declarations */
import { keyBy } from 'lodash';
import { combineReducers } from 'redux';
import { EQUAL_WEIGHTS_TITLE } from '../../../../common/model/data';
import { getNeutralValueSet } from '../../../../common/model/input/value-sets';
import { isTeamUser } from '../../../../common/utils';
import {
  getTeamSession,
  loadChatInternalMode,
  loadCompareRefs,
  loadModelVersion,
  loadRelease,
  loadToggledComponents,
  loadViewSettings,
  saveChatInternalMode,
  saveCompareRefs,
  saveModelVersion,
  saveRelease,
  saveUser,
  saveViewSettings,
} from '../../../common/generic/utilities/storage';
import { viewSettingOverridesForEnabledKeys, withViewSetting } from '../../../common/generic/view-settings/registry';
import { Action } from '../actions/actions';
import { initialContributionState, reducer as contributions } from '../modules/contribution';
import { initialModalState, reducer as modal } from '../modules/modal';
import { DataTree, RequestTree, SelectionsTree, StateTree, UserTree } from './types';

const initialUserState: UserTree = {
  publicUsersByUsername: {},
  publicUsersById: {},
  toggledUIComponents: loadToggledComponents(),
};

const initialRequestState: RequestTree = {
  ongoingRequests: [],
  failedRequests: [],
};

export function getInitialState() {
  const selectedModelVersion = getTeamSession() ? loadModelVersion() : undefined;
  const initialState: StateTree = {
    data: {
      machineLearning: {},
      time: new Date(),
    },
    selections: {
      valueSet: getNeutralValueSet(),
      valueSetName: EQUAL_WEIGHTS_TITLE,
      selectedModelVersion,
      selectedRelease: undefined,
      defaultVersion: undefined,
      defaultRelease: undefined,
      machineLearningView: {
        selectedClassifiers: [],
        certaintyFilterRange: [0.5, 1.0],
        certaintyFilterClassifier: 'consensus',
        offset: 0,
      },
      compareRefs: loadCompareRefs(),
      valuesSelectorOpen: false,
      // Initial value left as the empty blob; the real value is loaded from
      // localStorage inside the `USER/LOGGED_IN` reducer once we know the
      // current user is a team user. Hydrating before login would let
      // stale flags briefly leak into the UI for non-team users (PLT-4824).
      viewSettings: {},
      // PLT-4824: modal-open flag is not persisted; defaults to closed on
      // every page load.
      viewSettingsModalOpen: false,
      // Same defence-in-depth pattern as `viewSettings`, retained as a
      // dedicated field because the chat module uses its own lowercase
      // namespaced storage key. See PLT-4475.
      chatInternalMode: false,
    },
    user: initialUserState,
    modal: initialModalState,
    request: initialRequestState,
    contributions: initialContributionState,
  };
  return initialState;
}

function dataReducer(state: DataTree | undefined, action: Action): DataTree {
  state = state ?? getInitialState().data;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- a slice reducer handles only its own actions; `default` returns the state unchanged for the rest
  switch (action.type) {
    case 'STORE_IMPACT_SCORES':
      return {
        ...state,
        impactScoresByPIVV: {
          ...(state.impactScoresByPIVV || {}),
          ...action.payload.impactScoresByPIVV,
        },
      };
    case 'STORE_COMPANIES':
      return {
        ...state,
        companies: [...action.payload.companies],
      };
    case 'ADD_COMPANIES':
      return {
        ...state,
        companies: (state.companies || []).concat(action.payload.companies),
      };
    case 'UPDATE_COMPANY_NAME':
      return {
        ...state,
        companies: state.companies?.map(c => (c.id === action.payload.company.id ? { ...action.payload.company } : c)),
      };
    case 'STORE_PREDICTIONS_DATA':
      // eslint-disable-next-line no-case-declarations
      const { predictions } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          predictions,
        },
      };
    // PLT-4774: STORE_PRODUCTS / REPLACE_PRODUCTS / REMOVE_PRODUCTS were
    // removed — the slim product hierarchy now lives in React Query.
    case 'STORE_IMPACT_LABELS':
      return {
        ...state,
        impactLabels: {
          ...keyBy(action.payload.impactLabels, impactLabel => impactLabel.id),
        },
      };
    case 'STORE_GLOBAL_FIGURES':
      return {
        ...state,
        globalFigures: action.payload.figures,
      };
    case 'STORE_IMPACT_SIZE_FACTORS':
      return {
        ...state,
        impactSizeFactors: action.payload.factors,
      };
    case 'STORE_REFERENCE_FIGURES':
      return {
        ...state,
        referenceFigures: action.payload.figures,
      };
    case 'USER/LOGGED_OUT':
      return { ...getInitialState().data };
    default:
      return state;
  }
}

function selectionsReducer(state: SelectionsTree | undefined, action: Action): SelectionsTree {
  state = state ?? getInitialState().selections;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- a slice reducer handles only its own actions; `default` returns the state unchanged for the rest
  switch (action.type) {
    case 'SET_VALUE_SET':
      return {
        ...state,
        valueSet: {
          ...action.payload.valueSet,
        },
        valueSetName: action.payload.name,
      };
    case 'SET_CERTAINTY_FILTER_RANGE':
      return {
        ...state,
        machineLearningView: {
          ...state.machineLearningView,
          certaintyFilterRange: action.payload.range,
          offset: 0,
        },
      };
    case 'SET_CORRECT_LABEL_FILTER':
      return {
        ...state,
        machineLearningView: {
          ...state.machineLearningView,
          correctLabelFilter: action.payload.correctLabel,
          offset: 0,
        },
      };
    case 'SET_PREDICTED_LABEL_FILTER':
      return {
        ...state,
        machineLearningView: {
          ...state.machineLearningView,
          predictedLabelFilter: action.payload.predictedLabelFilter,
          offset: 0,
        },
      };
    case 'SET_SAMPLE_OFFSET':
      return {
        ...state,
        machineLearningView: {
          ...state.machineLearningView,
          offset: action.payload.offset,
        },
      };
    case 'SET_CERTAINTY_FILTER_CLASSIFIER':
      return {
        ...state,
        machineLearningView: {
          ...state.machineLearningView,
          certaintyFilterClassifier: action.payload.classifier,
        },
      };
    case 'SET_SELECTED_CLASSIFIERS':
      return {
        ...state,
        machineLearningView: {
          ...state.machineLearningView,
          selectedClassifiers: action.payload.classifiers,
          // TODO: this logic could be smarter
          certaintyFilterClassifier: 'consensus',
          predictedLabelFilter: undefined,
          correctLabelFilter: undefined,
        },
      };
    case 'COMPARE/ADD':
      const newCompareRefs = [...state.compareRefs, action.payload.ref];
      saveCompareRefs(newCompareRefs);
      return {
        ...state,
        compareRefs: newCompareRefs,
      };
    case 'COMPARE/REMOVE':
      const compareRefs = state.compareRefs.filter(
        ref => ref.id !== action.payload.ref.id || ref.type !== action.payload.ref.type,
      );
      saveCompareRefs(compareRefs);
      return {
        ...state,
        compareRefs,
      };
    case 'DEFAULT_VERSION_AND_RELEASE/SET':
      return {
        ...state,
        defaultRelease: action.payload.release,
        defaultVersion: action.payload.version,
      };
    case 'MODEL_VERSION_AND_RELEASE/SET':
      const { version, release } = action.payload;
      saveModelVersion(version);
      saveRelease(release);
      return {
        ...state,
        selectedModelVersion: version,
        selectedRelease: release,
      };
    case 'USER/LOGGED_IN':
      // Note that this action is not only called on login, but
      // also when user data is fetched with existing token.

      // Prefer model version stored in local storage
      // if one is available and user is team user
      const userState = isTeamUser(action.payload)
        ? {
            viewSettings: loadViewSettings(),
            chatInternalMode: loadChatInternalMode(),
            selectedModelVersion: loadModelVersion(),
            selectedRelease: loadRelease(),
          }
        : {};
      return {
        ...state,
        ...userState,
      };
    case 'USER/LOGGED_OUT':
      saveModelVersion('');
      return {
        ...getInitialState().selections,
      };
    case 'SET_VALUES_SELECTOR_OPEN':
      return {
        ...state,
        valuesSelectorOpen: action.payload.open,
      };
    case 'SET_VIEW_SETTING': {
      const { key, enabled } = action.payload;
      const next = withViewSetting(state.viewSettings, key, enabled);
      saveViewSettings(next);
      return {
        ...state,
        viewSettings: next,
      };
    }
    case 'APPLY_VIEW_SETTINGS_PROFILE': {
      const next = viewSettingOverridesForEnabledKeys(action.payload.enabledKeys);
      saveViewSettings(next);
      return {
        ...state,
        viewSettings: next,
      };
    }
    case 'SET_VIEW_SETTINGS_MODAL_OPEN':
      return {
        ...state,
        viewSettingsModalOpen: action.payload.open,
      };
    case 'SET_CHAT_INTERNAL_MODE':
      saveChatInternalMode(action.payload.enabled);
      return {
        ...state,
        chatInternalMode: action.payload.enabled,
      };
    default:
      return state;
  }
}

function userTree(state: UserTree | undefined, action: Action): UserTree {
  state = state ?? getInitialState().user;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- a slice reducer handles only its own actions; `default` returns the state unchanged for the rest
  switch (action.type) {
    case 'USER/LOGGED_IN':
      saveUser(action.payload);
      return {
        ...state,
        user: {
          ...action.payload,
          values: action.payload.values || getNeutralValueSet(),
        },
      };

    case 'USER/LOGGED_OUT':
      return {
        ...initialUserState,
      };

    case 'USER/STORE_VALUES':
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            values: action.payload.values,
          },
        };
      }
      return state;

    case 'USER/STORE_EXTRA_DATA':
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            extraData: { ...state.user.extraData, ...action.payload.extraData },
          },
        };
      }
      return state;

    case 'USER/STORE_PUBLIC_USERS':
      if (action.payload.users.length > 0) {
        const usersById = action.payload.users.reduce<UserTree['publicUsersById']>((result, user) => {
          result[user.id] = user;
          return result;
        }, {});
        const usersByUsername = action.payload.users.reduce<UserTree['publicUsersByUsername']>((result, user) => {
          result[user.username] = user;
          return result;
        }, {});

        return {
          ...state,
          publicUsersById: {
            ...state.publicUsersById,
            ...usersById,
          },
          publicUsersByUsername: {
            ...state.publicUsersByUsername,
            ...usersByUsername,
          },
        };
      }
      return state;

    case 'USER/SET_TOGGLED_COMPONENT':
      return {
        ...state,
        toggledUIComponents: {
          ...state.toggledUIComponents,
          ...action.payload.toggledComponents,
        },
      };
    default:
      return state;
  }
}

function requestTree(state: RequestTree | undefined, action: Action): RequestTree {
  state = state ?? getInitialState().request;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- a slice reducer handles only its own actions; `default` returns the state unchanged for the rest
  switch (action.type) {
    case 'REQUESTS/REQUEST_STARTED':
      return {
        ...state,
        failedRequests: state.failedRequests.filter(r => r.id !== action.payload.id),
        ongoingRequests: [...state.ongoingRequests, action.payload.id],
      };
    case 'REQUESTS/REQUEST_SUCCEEDED':
      return {
        ...state,
        ongoingRequests: state.ongoingRequests.filter(id => id !== action.payload.id),
      };
    case 'REQUESTS/REQUEST_FAILED':
      return {
        ...state,
        ongoingRequests: state.ongoingRequests.filter(id => id !== action.payload.id),
        failedRequests: [...state.failedRequests, action.payload],
      };
    case 'REQUESTS/CLEAR_FAILED':
      return {
        ...state,
        failedRequests: state.failedRequests.filter(({ id }) => id !== action.payload.id),
      };
    case 'USER/LOGGED_OUT':
      return {
        ...getInitialState().request,
      };
    default:
      return state;
  }
}

export default combineReducers<StateTree>({
  data: dataReducer,
  selections: selectionsReducer,
  user: userTree,
  request: requestTree,
  contributions,
  modal,
});

export * from './types';
